/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import CommentIcon from '@atlaskit/icon/core/comment';
import LinkIcon from '@atlaskit/icon/core/link';
import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { fetchLinkedJiraIssueUrls } from '@atlassian/search-client/linked-jira-issues';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { Expand, ExpandList } from '../../../../../../../common/ui/expand-list';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import {
	extractMarkdownAndAnchorLinkHrefs,
	getLinksFromText,
	LinkAttachments,
	buttonLabel as linkAttachmentsButtonLabel,
	mergeAndDedupeLinks,
	removeHtmlTags,
	removeUrlsFromText,
} from '../../../../../../../common/ui/link-attachments';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	onLinkedJiraIssuesFetchFail,
	onLinkedJiraIssuesFetchSuccess,
} from '../../../../../../../common/utils/events/linked-jira-issues-fetch';
import { isNonPublic } from '../../../../../../../common/utils/is-non-public';
import { getPullRequestStatusAppearance } from '../../../../../../../common/utils/pull-request-status';
import { removeMarkdown } from '../../../../../../../common/utils/remove-markdown';
import {
	enrichExcerpt,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useAnalytics } from '../../../../../../../controllers/analytics';
import { useAggAbsoluteUrl } from '../../../../../../../controllers/store/bootstrap';
// eslint-disable-next-line  @atlassian/tangerine/import/no-parent-imports
import { ThreadBody } from '../../common/thread-body';

import type { searchGraphPullRequest_AtlassianSearchPage$key } from './__generated__/searchGraphPullRequest_AtlassianSearchPage.graphql';
// import { GitBuildStatus } from '../git-build-status';
// import type { BuildStatus } from '../git-build-status/types';
import { messages } from './messages';

const styles = cssMap({
	commentsContent: {
		paddingInlineStart: token('space.150'),
	},
});

export const SearchGraphPullRequest = ({
	node,
	position,
	cloudId,
	primaryProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphPullRequest_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphPullRequest_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				url
				permissionLevel
				excerpt
				containerName @optIn(to: "SearchPerformanceImprovementChanges")
				lastModifiedDate
				title
				entity {
					... on ExternalPullRequest {
						title
						lastUpdate
						pullRequestId
						pullRequestStatus: status
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalRepository {
								name
							}
						}
					}
				}
				initialContributors {
					name
					picture
				}
				...threadBody_AtlassianSearchPage
				linkedEntities {
					id
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const {
		url,
		permissionLevel,
		entity,
		excerpt,
		containerName,
		lastModifiedDate,
		title: partialTitle,
		initialContributors,
	} = data;
	const {
		title: entityTitle,
		lastUpdate,
		container,
		pullRequestId,
		pullRequestStatus: status,
	} = entity || {};
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent =
		lastModifiedDate || lastUpdate ? (
			<LastModified lastModified={lastUpdate ?? lastModifiedDate} />
		) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	// Memoize the regex-heavy excerpt cleanup so we don't re-run the full
	// `removeHtmlTags` → `removeMarkdown` → `removeUrlsFromText` pipeline on
	// every parent re-render. The pipeline is bounded (excerpts are short and
	// regexes are linear-time) but unmemoized it would re-run every commit.
	//
	// PR descriptions can contain a restricted subset of HTML (e.g. `<img>`,
	// `<picture>`, `<details>`) and Markdown. For excerpt rendering we want
	// plain text. Order matters here:
	//   1. Strip HTML FIRST so media-asset URLs (`<img src="…">`) and tag
	//      markup don't appear inline. We must do this before `removeMarkdown`
	//      because that helper's "broken autolink" rule eats the leading `<`
	//      of any HTML tag, which would corrupt the tag and prevent us from
	//      removing it cleanly afterwards.
	//   2. Run `removeMarkdown` to flatten markdown link labels (`[label](url)`
	//      → `label`), inline code, headings, etc.
	//   3. Strip any remaining bare URLs — they're surfaced via the Links
	//      expand instead.
	// The Links expand reads from `cleanedExcerptForLinks` (after HTML +
	// markdown) so it stays in sync with what's visible in the body.
	const stripLinksFromExcerpt = fg('rovo_fps_pr_strip_links_from_excerpt');

	const cleanedExcerptForLinks = useMemo(() => {
		if (!stripLinksFromExcerpt) {
			return excerpt || '';
		}
		return removeMarkdown(removeHtmlTags(excerpt || ''));
	}, [excerpt, stripLinksFromExcerpt]);

	const descriptionText = useMemo(() => {
		if (stripLinksFromExcerpt) {
			// `cleanedExcerptForLinks` is already `removeMarkdown(removeHtmlTags(...))`,
			// so we just need the final URL-stripping pass on top.
			return processExcerpt(removeUrlsFromText(cleanedExcerptForLinks), true);
		}
		return processExcerpt(removeMarkdown(excerpt), true);
	}, [excerpt, cleanedExcerptForLinks, stripLinksFromExcerpt]);

	const isPrCommentsEnabled = fg('rovo_fps_enable_pr_comments');

	// TODO: Wire up real build status data when GraphQL schema is available
	// const isCiCdEnabled = fg('rovo_fps_enable_git_ui_ci_cd');
	// const buildStatusComponent = isCiCdEnabled ? (
	// 	<GitBuildStatus build={placeholderBuildStatus} testId="pull-request-build-status" />
	// ) : null;

	const buildStatusComponent = null;

	const maxComments = 5;

	const statusLabel = status
		? formatMessage(
				messages[`status_${status.toLowerCase()}` as keyof typeof messages] ?? messages.resultLabel,
			)
		: null;
	const statusLozenge =
		status && statusLabel ? (
			<Lozenge
				appearance={getPullRequestStatusAppearance(status)}
				testId="pull-request-status-lozenge"
				isBold
			>
				{statusLabel}
			</Lozenge>
		) : null;

	const titleMetadataParts: [string, React.ReactNode][] = statusLozenge
		? [['status', statusLozenge]]
		: [];

	if (buildStatusComponent) {
		titleMetadataParts.unshift(['build-status', buildStatusComponent]);
	}

	const headerMetadataParts = [
		label,
		['pull-request-id', pullRequestId ? `#${pullRequestId}` : null],
		['repository-name', container?.name ?? containerName ?? null],
		['padlock', padlockIcon],
		['last-modified', lastModifiedComponent],
	];

	// Add contributor if they exist
	const initialContributor = initialContributors?.[0] ?? null;
	const contributorName = initialContributor?.name?.trim() || null;

	if (contributorName) {
		const contributorContent = (
			<Inline space="space.025" alignBlock="start">
				<ProfileAvatar name={contributorName} picture={initialContributor?.picture} />
				<Box>{contributorName}</Box>
			</Inline>
		);
		headerMetadataParts.push(['author', contributorContent]);
	}

	// Fetch linked Jira issue URLs from UGS
	const [aggAbsoluteUrl] = useAggAbsoluteUrl();
	const { fireAnalyticsEvent } = useAnalytics();
	const [linkedJiraIssueUrls, setLinkedJiraIssueUrls] = useState<string[]>([]);

	useEffect(() => {
		if (!cloudId || !data.id || !data.id.includes(':pull-request/')) {
			return;
		}

		let cancelled = false;
		const queryContext = `ari:cloud:jira::site/${cloudId}`;
		const headers = buildAtlAttributionHeaders({ cloudId, product: primaryProduct });

		fetchLinkedJiraIssueUrls(data.id, queryContext, { aggAbsoluteUrl, headers })
			.then((urls) => {
				if (!cancelled) {
					setLinkedJiraIssueUrls(urls);
					fireAnalyticsEvent(onLinkedJiraIssuesFetchSuccess({ issueCount: urls.length }));
				}
			})
			.catch((error: unknown) => {
				fireAnalyticsEvent(
					onLinkedJiraIssuesFetchFail({
						reason: error instanceof Error ? error.message : String(error),
					}),
				);
			});

		return () => {
			cancelled = true;
		};
	}, [cloudId, data.id, aggAbsoluteUrl, primaryProduct, fireAnalyticsEvent]);

	// Pull URLs out of "rich" link carriers (`[label](url)` markdown links and
	// `<a href="…">` HTML anchors) BEFORE the cleanup passes consume them.
	// Otherwise removing markdown / HTML would silently drop these links.
	// Image URLs (`<img src>`, `![alt](url)`) are intentionally excluded — those
	// are assets, not navigable links.
	//
	// Also memoizes the bare-URL extraction over the cleaned excerpt and the
	// final merge so repeat renders don't re-walk the regexes.
	const richExcerptLinks: string[] = useMemo(
		() => (stripLinksFromExcerpt ? extractMarkdownAndAnchorLinkHrefs(excerpt || '') : []),
		[excerpt, stripLinksFromExcerpt],
	);

	const excerptLinks: string[] = useMemo(
		() => getLinksFromText(cleanedExcerptForLinks),
		[cleanedExcerptForLinks],
	);

	// Merge UGS-hydrated Jira issue URLs with rich-link hrefs and bare excerpt
	// URLs. `mergeAndDedupeLinks` canonicalizes URLs for deduplication so
	// variants of the same resource (trailing slash, host casing, etc.) collapse
	// into a single entry — UGS-hydrated URLs win order so they appear first.
	const links = useMemo(
		() => mergeAndDedupeLinks(linkedJiraIssueUrls, richExcerptLinks, excerptLinks),
		[linkedJiraIssueUrls, richExcerptLinks, excerptLinks],
	);

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const commentCount = isPrCommentsEnabled ? (data.linkedEntities?.length ?? 0) : 0;
	const hasComments = commentCount > 0;

	const showExpandList = hasComments || links.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		<>
			<MentionedByLinkedResults
				linkedResults={linkedResults || []}
				thirdPartyConfigs={thirdPartyConfigs}
				partial={data}
				url={url}
				position={position}
				onResultClicked={onResultClicked}
				onResultContextMenu={onResultContextMenu}
				isAllProductSelected={!selectedProduct}
			/>
		</>
	) : showExpandList ? (
		<ExpandList>
			<Expand
				icon={CommentIcon}
				attachmentCount={commentCount}
				attachmentType="comment"
				label={formatMessage(messages.commentsLabel, { count: commentCount })}
			>
				<Box xcss={styles.commentsContent}>
					<ThreadBody node={data} showParent={false} maxComments={maxComments} />
				</Box>
			</Expand>
			{links.length > 0 && (
				<Expand
					icon={LinkIcon}
					attachmentCount={links.length}
					attachmentType="url"
					label={formatMessage(linkAttachmentsButtonLabel, { count: links.length })}
				>
					<LinkAttachments links={links} onResultClicked={onResultClicked} />
				</Expand>
			)}
		</ExpandList>
	) : null;

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={descriptionText} />}
			headerMetadataParts={headerMetadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} displaySpacer={false} />}
			title={enrichExcerpt(partialTitle ?? entityTitle)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
